import { getLanguageCode } from 'js/lib/language';

import type { PartnerCourseCardFragment } from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerCourseCardsQuery';

type CourseStatus = 'launched' | 'preenroll' | 'unknown';

function getCourseStatus(course: PartnerCourseCardFragment): CourseStatus {
  if (course.launchedAt) return 'launched';
  if (course.preEnrollmentEnabledAt) return 'preenroll';
  return 'unknown';
}

export function sortCourses<T extends PartnerCourseCardFragment>(courses: T[]): T[] {
  const sorted = [...courses];

  // Sort alphabetically by name
  sorted.sort((a, b) => a.name.localeCompare(b.name));

  // Boost courses matching user's language to the top
  sorted.sort((a, b) => {
    const boostLanguage = getLanguageCode();

    const aHasBoostLanguage = a.primaryLanguageCodes.includes(boostLanguage);
    const bHasBoostLanguage = b.primaryLanguageCodes.includes(boostLanguage);

    if (aHasBoostLanguage && !bHasBoostLanguage) return -1;
    if (!aHasBoostLanguage && bHasBoostLanguage) return 1;
    return 0;
  });

  // Sort launched before preenroll; preenroll sorted by plannedLaunchDate
  sorted.sort((a, b) => {
    const statusOrder: CourseStatus[] = ['launched', 'preenroll', 'unknown'];

    const aStatus = getCourseStatus(a);
    const bStatus = getCourseStatus(b);

    const statusComparison = statusOrder.indexOf(aStatus) - statusOrder.indexOf(bStatus);

    if (statusComparison !== 0) {
      return statusComparison;
    }

    if (aStatus === 'preenroll' && bStatus === 'preenroll' && a.plannedLaunchDate && b.plannedLaunchDate) {
      const dateA = new Date(a.plannedLaunchDate).getTime();
      const dateB = new Date(b.plannedLaunchDate).getTime();
      return dateA - dateB;
    }

    return 0;
  });

  return sorted;
}
